import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { User, LogOut, ChevronDown, Settings } from "lucide-react";
import "../styles/NavBar.css";
import logo from "../logo.png";
import { useAuth } from "../components/AuthContext.js";
import axiosInstance from "../api/api.js";

const NavBar = ({ role }) => {
  const { isAuthenticated, role: userRole, id, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [greeting, setGreeting] = useState("");
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user details when component mounts or id changes
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axiosInstance.get(`/employees/${id}`);
        setUserDetails(response.data);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch user details:", err);
        setError("Failed to load user details");
        setUserDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [id]);

  // Set time-based greeting
  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      let newGreeting = "";

      if (hour < 12) {
        newGreeting = "Good Morning";
      } else if (hour < 18) {
        newGreeting = "Good Afternoon";
      } else {
        newGreeting = "Good Evening";
      }

      setGreeting(newGreeting);
    };

    updateGreeting();
    // Update greeting every minute in case the page is open for a long time
    const interval = setInterval(updateGreeting, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Check if link is active
  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Use the role prop if available, otherwise use the one from auth context
  const currentRole = role || userRole;

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo */}
        <div className="navbar-title">
          <img src={logo} alt="Logo" className="navbar-logo" />
        </div>

        <div className="navbar-links">
          {currentRole === "admin" ? (
            // Admin-specific links
            <>
              <Link
                to="/admin-dashboard"
                className={`navbar-link ${
                  isActive("/admin-dashboard") ? "active" : ""
                }`}
              >
                Dashboard
              </Link>
              <Link
                to="/members-profile"
                className={`navbar-link ${
                  isActive("/members-profile") ? "active" : ""
                }`}
              >
                Employees
              </Link>
              <Link
                to="/manual-adjust"
                className={`navbar-link ${
                  isActive("/manual-adjust") ? "active" : ""
                }`}
              >
                Manual Adjust
              </Link>
              <Link
                to="/calendar"
                className={`navbar-link ${
                  isActive("/calendar") ? "active" : ""
                }`}
              >
                Leaves
              </Link>
              <Link
                to="/emp-reports"
                className={`navbar-link ${
                  isActive("/emp-reports") ? "active" : ""
                }`}
              >
                Reports
              </Link>
              <Link
                to="/admin-notification"
                className={`navbar-link ${
                  isActive("/admin-notification") ? "active" : ""
                }`}
              >
                Notification
              </Link>
            </>
          ) : currentRole === "outsource" ? (
            // Outsource-specific links
            <>
              <Link
                to="/outsource-dashboard"
                className={`navbar-link ${
                  isActive("/outsource-dashboard") ? "active" : ""
                }`}
              >
                Dashboard
              </Link>
              <Link
                to="/outsource-reports"
                className={`navbar-link ${
                  isActive("/outsource-reports") ? "active" : ""
                }`}
              >
                Reports
              </Link>
              <Link
                to="/outsource-notifications"
                className={`navbar-link ${
                  isActive("/outsource-notifications") ? "active" : ""
                }`}
              >
                Notifications
              </Link>
            </>
          ) : (
            // Employee-specific links
            <>
              <Link
                to="/dashboard"
                className={`navbar-link ${
                  isActive("/dashboard") ? "active" : ""
                }`}
              >
                Dashboard
              </Link>
              <Link
                to="/report"
                className={`navbar-link ${isActive("/report") ? "active" : ""}`}
              >
                Report
              </Link>
              <Link
                to="/leave"
                className={`navbar-link ${isActive("/leave") ? "active" : ""}`}
              >
                Leave
              </Link>
              <Link
                to="/notifications"
                className={`navbar-link ${
                  isActive("/notifications") ? "active" : ""
                }`}
              >
                Notifications
              </Link>
            </>
          )}
        </div>

        {/* User Info with Dropdown */}
        {isAuthenticated && (
          <div className="navbar-user" ref={dropdownRef}>
            {loading ? (
              <div className="navbar-profile-loading">
                <div className="navbar-avatar-loading"></div>
                <div className="navbar-user-info-loading">
                  <div className="loading-line-short"></div>
                  <div className="loading-line-long"></div>
                </div>
              </div>
            ) : error ? (
              <div className="navbar-profile-error">
                <div className="navbar-avatar">!</div>
                <div className="navbar-user-info-error">
                  Error loading profile
                </div>
              </div>
            ) : userDetails ? (
              <>
                <div className="navbar-profile" onClick={toggleDropdown}>
                  <div className="navbar-avatar">
                    {userDetails.user.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="navbar-user-info">
                    <span className="navbar-user-greeting">{greeting}</span>
                    <span className="navbar-user-name">
                      {userDetails.user.name}
                    </span>
                    <span className="navbar-user-position">
                      {userDetails.user.position}
                    </span>
                  </div>
                  <ChevronDown
                    className={`navbar-dropdown-arrow ${
                      isDropdownOpen ? "open" : ""
                    }`}
                    size={16}
                  />
                </div>

                {isDropdownOpen && (
                  <div className="navbar-dropdown">
                    <Link
                      to="/profile"
                      className={`navbar-dropdown-item ${
                        isActive("/profile") ? "active" : ""
                      }`}
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <User size={16} />
                      Profile
                    </Link>

                    {userRole === "admin" && (
                      <>
                        <div className="navbar-dropdown-divider"></div>
                        <Link
                          to="/admin-settings"
                          className={`navbar-dropdown-item ${
                            isActive("/admin-settings") ? "active" : ""
                          }`}
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <Settings size={16} />
                          Settings
                        </Link>
                      </>
                    )}

                    <div className="navbar-dropdown-divider"></div>
                    <button
                      className="navbar-dropdown-item logout-item"
                      onClick={handleLogout}
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavBar;
